import { useRef, useState } from 'react';

import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
  Button as MuiButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import MobileKeyboard, {
  KEYBOARD_H,
} from '@/designs-dev/device/MobileKeyboard';
import HomeIndicator, {
  HOME_INDICATOR_H,
} from '@/designs-dev/device/HomeIndicator';
import StatusBar from '@/designs-dev/device/StatusBar';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// Pure-UI mock of the multi-step KareHero sign-up (Figma "4-step, combined name").
// No real auth — production stays on Kinde's hosted flow. This exists to design
// and user-test the experience inside the phone frame.

const TOTAL_DOTS = 6;
const STEP_TITLES = [
  'What’s your email?',
  'What’s your name?',
  'Check your email for a code',
  'Choose your password',
];
const TOTAL_STEPS = STEP_TITLES.length;

// spacing-11 above the title (sits below the back/progress row).
const CONTENT_TOP_GAP = '80px';
// Horizontal screen gutter (24px — comfortable above iOS's 16px baseline).
const SCREEN_PX = 3;

const ProgressDots = ({ active }: { active: number }) => (
  <Stack direction='row' spacing={0.75}>
    {Array.from({ length: TOTAL_DOTS }).map((_, i) => (
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: i < active ? 'primary.main' : 'custom.neutral.300',
        }}
        key={i}
      />
    ))}
  </Stack>
);

const SignUpFlow = ({
  onExit,
  onComplete,
}: {
  onExit?: () => void;
  onComplete?: () => void;
}) => {
  const [step, setStep] = useState(0);
  const [kbOpen, setKbOpen] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  // Move between fields without the keyboard flickering: a short blur delay that
  // a following focus cancels.
  const onFocus = () => {
    window.clearTimeout(blurTimer.current);
    setKbOpen(true);
  };
  const onBlur = () => {
    blurTimer.current = window.setTimeout(() => setKbOpen(false), 120);
  };
  const fieldProps = {
    fullWidth: true,
    onFocus,
    onBlur,
    slotProps: { inputLabel: { shrink: true } },
  } as const;

  const next = () => {
    setKbOpen(false);
    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const back = () => {
    setKbOpen(false);
    if (step === 0) (onExit ?? (() => (window.location.href = '/')))();
    else setStep((s) => s - 1);
  };

  // Edge-swipe back: drag rightward from the left edge to pop the step, with an
  // iOS-style parallax — the previous screen slides in underneath.
  const SCREEN_W = 375; // PhoneFrame screen width
  const SWIPE_EDGE = 40; // gesture must start within this many px of the left edge
  const SWIPE_THRESHOLD = 80; // drag past this to commit the back
  const SETTLE_MS = 230;
  const rootRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [gestureActive, setGestureActive] = useState(false);
  const [noTransition, setNoTransition] = useState(false);
  const [overEdge, setOverEdge] = useState(false); // hovering the left drag strip

  const onMouseMove = (e: React.MouseEvent) => {
    if (dragStart.current) return;
    const rect = rootRef.current?.getBoundingClientRect();
    setOverEdge(!!rect && e.clientX - rect.left <= SWIPE_EDGE);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect || e.clientX - rect.left > SWIPE_EDGE) return;
    dragStart.current = { x: e.clientX, y: e.clientY };
    setDragging(true);
    setGestureActive(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dy) > Math.abs(dx) + 12) {
      // mostly vertical — let it scroll, abandon the swipe
      dragStart.current = null;
      setDragging(false);
      setGestureActive(false);
      setDragX(0);
      return;
    }
    setDragX(Math.max(0, Math.min(dx, SCREEN_W)));
  };
  const endDrag = () => {
    if (!dragStart.current) return;
    dragStart.current = null;
    setDragging(false); // enable the settle transition
    const commit = dragX > SWIPE_THRESHOLD;
    if (commit && step === 0) {
      setDragX(0);
      setGestureActive(false);
      back();
      return;
    }
    if (commit) {
      setDragX(SCREEN_W); // current slides off right; previous slides to 0
      window.setTimeout(() => {
        setNoTransition(true);
        setStep((s) => s - 1);
        setDragX(0);
        setGestureActive(false);
        window.setTimeout(() => setNoTransition(false), 30);
      }, SETTLE_MS);
    } else {
      setDragX(0); // spring back
      window.setTimeout(() => setGestureActive(false), SETTLE_MS);
    }
  };

  const layerTransition =
    dragging || noTransition ? 'none' : `transform ${SETTLE_MS - 10}ms ease`;
  const showPrev = gestureActive && step > 0;

  // One step's screen, parameterised so the previous step can render underneath
  // during a back-swipe.
  const renderScreen = (s: number) => (
    <>
      <StatusBar />

      {/* header: back + progress */}
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.5,
          flexShrink: 0,
        }}
        direction='row'
      >
        <Button
          variant='link'
          ariaLabel='Go back'
          iconLeft='chevron-left'
          onPress={back}
        >
          Back
        </Button>
        <Box sx={{ pr: 1.5 }}>
          <ProgressDots active={s + 1} />
        </Box>
      </Stack>

      {/* body (80px / spacing-11 above the title) */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          px: SCREEN_PX,
          pt: CONTENT_TOP_GAP,
          pb: `${KEYBOARD_H + 96}px`,
        }}
      >
        <Typography sx={{ textAlign: 'center', mb: 10 }} variant='h1'>
          {STEP_TITLES[s]}
        </Typography>

        {s === 0 && (
          <Stack spacing={3}>
            <MuiButton
              fullWidth
              size='large'
              variant='contained'
              startIcon={
                <Box
                  sx={{ width: 18, height: 18 }}
                  component='img'
                  src='/vector/google.svg'
                  alt=''
                />
              }
              sx={{
                bgcolor: 'common.black',
                color: 'common.white',
                textTransform: 'none',
                py: 1.25,
                borderRadius: '8px',
                '&:hover': { bgcolor: 'common.black' },
              }}
            >
              Continue with Google
            </MuiButton>
            <Divider
              sx={{
                '&::before, &::after': { borderColor: 'custom.neutral.400' },
              }}
            >
              <Typography variant='body2' color='text.secondary'>
                or
              </Typography>
            </Divider>
            <TextField
              label='Personal email address'
              type='email'
              {...fieldProps}
            />
          </Stack>
        )}

        {s === 1 && (
          <Stack spacing={2}>
            <TextField label='First name' {...fieldProps} />
            <TextField label='Last name' {...fieldProps} />
          </Stack>
        )}

        {s === 2 && (
          <Stack spacing={1.5}>
            <TextField label='Code' inputMode='numeric' {...fieldProps} />
            <Typography variant='body2' color='text.secondary'>
              We sent a 6-digit code to your email. Didn’t get it?{' '}
              <Link
                component='button'
                type='button'
                variant='body2'
                sx={{ verticalAlign: 'baseline' }}
              >
                Resend
              </Link>
            </Typography>
          </Stack>
        )}

        {s === 3 && (
          <Stack spacing={2}>
            <TextField
              label='Password'
              type={showPw ? 'text' : 'password'}
              {...fieldProps}
              slotProps={{
                ...fieldProps.slotProps,
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label={showPw ? 'Hide password' : 'Show password'}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowPw((p) => !p)}
                        edge='end'
                      >
                        <Icon
                          icon={showPw ? ('eye-slash' as never) : 'eye'}
                          fontSize={16}
                        />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label='Confirm password'
              type='password'
              {...fieldProps}
            />
          </Stack>
        )}
      </Box>

      {/* footer: continue button rises above the keyboard when it's open */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: kbOpen ? KEYBOARD_H : HOME_INDICATOR_H,
          px: SCREEN_PX,
          pt: 1.5,
          pb: 1.5,
          transition: 'bottom 240ms ease',
          backgroundColor: (theme) =>
            alpha(theme.palette.background.default, 0.72),
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        }}
      >
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel='Continue'
          onPress={next}
        >
          Continue
        </Button>
      </Box>

      {/* bottom safe area — covered by the keyboard when it's open */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.default',
        }}
      >
        <HomeIndicator />
      </Box>
    </>
  );

  // Success screen after the final step.
  if (step === TOTAL_STEPS) {
    return (
      <ScreenRoot>
        <StatusBar />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            px: 3,
          }}
        >
          <Box sx={{ color: 'primary.main', mb: 2 }}>
            <Icon icon='check-circle' fontSize={56} />
          </Box>
          <Typography sx={{ mb: 1 }} variant='h2'>
            You’re all set
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Your KareHero account is ready.
          </Typography>
        </Box>
        <Box sx={{ px: SCREEN_PX }}>
          <Button
            variant='primary'
            size='lg'
            isFullWidth
            ariaLabel='Continue to KareHero'
            onPress={() => (onComplete ?? (() => setStep(0)))()}
          >
            Continue to KareHero
          </Button>
        </Box>
        <HomeIndicator />
      </ScreenRoot>
    );
  }

  return (
    <Box
      ref={rootRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onMouseMove={onMouseMove}
      onMouseLeave={() => setOverEdge(false)}
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: 'background.default',
        touchAction: 'pan-y',
        cursor: dragging ? 'grabbing' : overEdge ? 'grab' : 'default',
      }}
    >
      {/* previous screen sliding in underneath (parallax) */}
      {showPrev && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            transform: `translateX(${(dragX - SCREEN_W) * 0.3}px)`,
            transition: layerTransition,
            pointerEvents: 'none',
          }}
        >
          {renderScreen(step - 1)}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'common.black',
              opacity: Math.max(0, 0.25 * (1 - dragX / SCREEN_W)),
              pointerEvents: 'none',
            }}
          />
        </Box>
      )}

      {/* current screen, follows the finger */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          transform: `translateX(${dragX}px)`,
          transition: layerTransition,
          borderLeft: dragX > 0 ? '1px solid' : 0,
          borderColor: 'custom.neutral.300',
        }}
      >
        {renderScreen(step)}
      </Box>

      <MobileKeyboard visible={kbOpen} />
    </Box>
  );
};

export default SignUpFlow;
